import React from 'react';
import { FaCheck, FaClock, FaUsers, FaTag, FaCrown, FaShieldAlt, FaCar, FaCalendarAlt } from 'react-icons/fa';
import Card from './Card';
import { useCurrency } from '../../context/CurrencyContext';
import { formatPriceSync } from '../../utils/currency';

const formatOptionDate = (d) => {
  if (!d) return null;
  const x = new Date(d);
  return isNaN(x.getTime()) ? null : x.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
};

const OptionSelection = ({ 
  activityOptions, 
  selectedOption, 
  handleOptionSelect,
  selectedCountry 
}) => {
  const { currency } = useCurrency();
  const features = [
    { icon: FaClock, label: 'Flexible Duration' },
    { icon: FaUsers, label: 'Group Discounts' },
    { icon: FaShieldAlt, label: 'Insurance Included' },
    { icon: FaCrown, label: 'Premium Experience' },
  ];

  return (
    <div className="space-y-8">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        {features.map((feature, index) => (
          <div key={index} className="bg-slate-50 p-4 rounded-xl border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white rounded-lg border border-slate-200">
                <feature.icon className="text-blue-600 text-sm" />
              </div>
              <span className="text-sm font-medium text-slate-700">{feature.label}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5 sm:gap-6">
        {activityOptions.map((option, index) => (
          <Card
            key={option._id}
            selected={selectedOption?._id === option._id}
            onClick={() => handleOptionSelect(option)}
            className={index === 1 ? 'relative overflow-hidden' : ''}
          >
            {index === 1 && (
              <div className="absolute top-0 right-0 bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-1 rounded-bl-lg">
                <span className="text-xs font-bold">MOST POPULAR</span>
              </div>
            )}
            
            <div className="p-5 sm:p-6">
              <div className="flex justify-between items-start gap-4 mb-4">
                <div className="min-w-0">
                  <h3 className="text-lg font-bold text-gray-900 mb-1">
                    {option.name}
                  </h3>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-100 text-blue-800">
                      {option.active ? "Available" : "Limited"}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${option.includesTransfers ? "bg-teal-100 text-teal-800" : "bg-slate-100 text-slate-700"}`}>
                      <FaCar className="text-[10px]" />
                      {option.includesTransfers ? "With Transfers" : "Without Transfers"}
                    </span>
                    {option.maxCapacity > 10 && (
                      <span className="text-xs px-2 py-1 bg-emerald-100 text-emerald-800 rounded-full">
                        Group friendly
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className="text-xl sm:text-2xl font-bold text-gray-900">
                    {formatPriceSync(Number(option.basePrice || 0), currency)}
                  </div>
                  <div className="text-xs text-slate-500">per person</div>
                </div>
              </div>

              <div className="space-y-2.5 mb-5 text-sm text-slate-600">
                {(option.bookingStartDate || option.bookingEndDate) && (
                  <div className="flex items-start gap-2 p-2.5 bg-amber-50 rounded-lg border border-amber-100">
                    <FaCalendarAlt className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <span>
                      <span className="font-medium text-amber-800">Bookable: </span>
                      {option.bookingStartDate && option.bookingEndDate
                        ? `${formatOptionDate(option.bookingStartDate)} to ${formatOptionDate(option.bookingEndDate)}`
                        : option.bookingStartDate
                        ? `From ${formatOptionDate(option.bookingStartDate)}`
                        : `Until ${formatOptionDate(option.bookingEndDate)}`}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2">
                  <FaClock className="text-blue-500 flex-shrink-0" />
                  <span>{option.duration} min</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaUsers className="text-emerald-500 flex-shrink-0" />
                  <span>Max {option.maxCapacity} people</span>
                </div>
                <div className="flex items-center gap-2">
                  <FaTag className="text-purple-500 flex-shrink-0" />
                  <span>Free cancellation within 24h</span>
                </div>
              </div>

              <div className="mb-5 p-4 bg-slate-50 rounded-xl border border-slate-100">
                <h4 className="text-sm font-semibold text-gray-900 mb-2">What's included</h4>
                <ul className="text-sm text-slate-600 space-y-1.5">
                  <li className="flex items-center gap-2">
                    <FaCheck className="text-green-500 flex-shrink-0" size={12} />
                    Professional guide
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheck className="text-green-500 flex-shrink-0" size={12} />
                    Equipment rental
                  </li>
                  <li className="flex items-center gap-2">
                    <FaCheck className="text-green-500 flex-shrink-0" size={12} />
                    Safety briefing
                  </li>
                  <li className="flex items-center gap-2">
                    {option.includesTransfers ? (
                      <>
                        <FaCar className="text-teal-500 flex-shrink-0" size={12} />
                        Hotel/venue transfers included
                      </>
                    ) : (
                      <>
                        <span className="text-slate-400 flex-shrink-0">—</span>
                        No transfers (self travel)
                      </>
                    )}
                  </li>
                </ul>
              </div>

              <div
                className={`w-full py-3.5 rounded-xl font-semibold text-sm transition-all duration-200 flex items-center justify-center pointer-events-none ${
                  selectedOption?._id === option._id
                    ? "bg-blue-600 text-white shadow-md"
                    : "bg-slate-100 text-slate-700"
                }`}
              >
                {selectedOption?._id === option._id ? (
                  <>
                    <FaCheck className="mr-2" />
                    Selected – Continue to guests
                  </>
                ) : (
                  "Select package"
                )}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {activityOptions.length === 0 && (
        <div className="text-center py-14 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-300">
          <FaTag className="w-14 h-14 text-slate-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No packages available</h3>
          <p className="text-slate-600 text-sm">Use Back to choose another activity.</p>
        </div>
      )}
    </div>
  );
};

export default OptionSelection;